import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import Footer from '../components/Footer';
import UsageMeter from '../components/UsageMeter';
import SampleDataButton from '../components/SampleDataButton';
import { startProductTour } from '../components/ProductTour';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Download, Trash2, User, Bell, Shield, Home, PlayCircle, Sparkles, Globe, ExternalLink, Copy, Check, Rss } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

// ==== Public Sharing helper card ====
const PublicSharingCard = () => {
  const { currentWorkspace, refreshUsage } = useWorkspace();
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const toggle = async (field, value) => {
    if (!currentWorkspace) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/workspaces/${currentWorkspace.id}`, { [field]: value });
      toast.success(value ? `${field === 'public_roadmap' ? 'Roadmap' : 'Changelog'} is now public` : `${field === 'public_roadmap' ? 'Roadmap' : 'Changelog'} is now private`);
      await refreshUsage();
    } catch (err) {
      toast.error('Failed to update workspace');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!currentWorkspace) return null;

  const slug = currentWorkspace.slug;
  const origin = window.location.origin;
  const roadmapUrl = `${origin}/r/${slug}`;
  const changelogUrl = `${origin}/c/${slug}`;
  const rssUrl = `${API_URL}/api/public/changelog/${slug}/rss`;

  return (
    <Card className="p-6" data-testid="settings-public-sharing-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="rounded-full bg-primary/10 p-2">
          <Globe className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Public sharing</h2>
          <p className="text-sm text-muted-foreground">
            Make this workspace's roadmap and changelog publicly viewable at shareable URLs.
          </p>
        </div>
      </div>

      <div className="space-y-5">
        {/* Public Roadmap */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium">Public roadmap</h3>
              {currentWorkspace.public_roadmap && (
                <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-600">Live</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Anyone with the link can view your planned / in-progress / shipped items.
            </p>
            {currentWorkspace.public_roadmap && slug && (
              <div className="mt-3 flex items-center gap-2">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1 truncate" data-testid="public-roadmap-url">
                  {roadmapUrl}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(roadmapUrl, 'roadmap')}
                  className="h-8 px-2 gap-1"
                  data-testid="copy-roadmap-url"
                >
                  {copiedField === 'roadmap' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(roadmapUrl, '_blank')}
                  className="h-8 px-2 gap-1"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
          <Switch
            checked={!!currentWorkspace.public_roadmap}
            onCheckedChange={(v) => toggle('public_roadmap', v)}
            disabled={saving}
            data-testid="toggle-public-roadmap"
          />
        </div>

        <div className="border-t" />

        {/* Public Changelog */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium">Public changelog</h3>
              {currentWorkspace.public_changelog && (
                <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-600">Live</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Public URL + RSS feed of shipped features. Great for /buildinpublic.
            </p>
            {currentWorkspace.public_changelog && slug && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1 truncate" data-testid="public-changelog-url">
                    {changelogUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(changelogUrl, 'changelog')}
                    className="h-8 px-2 gap-1"
                    data-testid="copy-changelog-url"
                  >
                    {copiedField === 'changelog' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(changelogUrl, '_blank')}
                    className="h-8 px-2 gap-1"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1 truncate flex items-center gap-1.5">
                    <Rss className="h-3 w-3 text-orange-500" />
                    {rssUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(rssUrl, 'rss')}
                    className="h-8 px-2 gap-1"
                    data-testid="copy-rss-url"
                  >
                    {copiedField === 'rss' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <Switch
            checked={!!currentWorkspace.public_changelog}
            onCheckedChange={(v) => toggle('public_changelog', v)}
            disabled={saving}
            data-testid="toggle-public-changelog"
          />
        </div>
      </div>
    </Card>
  );
};


const SettingsPage = () => {
  const navigate = useNavigate();
  const { currentWorkspaceId } = useWorkspace();
  const [displayName, setDisplayName] = useState('Demo User');
  const [email, setEmail] = useState('demo@vibesprint.ai');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/user/export`, {
        responseType: 'blob',
        params: { workspace_id: currentWorkspaceId },
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vibesprint-data-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Data exported successfully', {
        description: 'Your data has been downloaded as JSON'
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed', {
        description: 'Unable to export data. Please try again.'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteDialog(true);
  };

  const confirmDeleteAccount = () => {
    // For MVP: Show contact message instead of actual deletion
    toast.info('Account Deletion Request', {
      description: 'Please email support@vibesprint.ai to delete your account. We\'ll process your request within 24 hours.',
      duration: 7000
    });
    setShowDeleteDialog(false);
  };

  const handleSaveProfile = () => {
    // For MVP: Just show success message (no actual backend save yet)
    toast.success('Settings saved', {
      description: 'Your preferences have been updated'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="text-xl font-bold text-foreground">
                VibeSprint
              </a>
              <Badge variant="secondary" className="text-xs">
                Demo
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/app')}
                className="gap-2"
                data-testid="settings-dashboard-link"
              >
                <Home className="h-4 w-4" />
                Dashboard
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account preferences and data
          </p>
        </div>

        <div className="space-y-6">
          {/* Usage & Plan */}
          <div data-testid="settings-usage-section">
            <UsageMeter variant="full" />
          </div>

          {/* Profile Section */}
          <Card className="p-6" data-testid="settings-profile-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="rounded-full bg-primary/10 p-2">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Profile</h2>
                <p className="text-sm text-muted-foreground">
                  Your personal information
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  className="mt-1.5"
                  data-testid="settings-display-name-input"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  This name will be used throughout the app
                </p>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1.5"
                  disabled
                  data-testid="settings-email-input"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Authentication will be available soon. Email is read-only for now.
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveProfile} data-testid="settings-save-profile-button">
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>

          {/* Notifications Section */}
          <Card className="p-6" data-testid="settings-notifications-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="rounded-full bg-primary/10 p-2">
                <Bell className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Notifications</h2>
                <p className="text-sm text-muted-foreground">
                  Manage how you receive updates
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <Label htmlFor="email-notifications" className="text-base font-medium cursor-pointer">
                    Email Notifications
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Receive updates about important decisions and risks
                  </p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                  data-testid="settings-email-notifications-toggle"
                />
              </div>

              <Separator />

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Coming Soon:</strong> Email notifications will be available when account authentication is enabled. Your preferences will be saved.
                </p>
              </div>
            </div>
          </Card>

          {/* Onboarding / Getting Started Section */}
          <Card className="p-6" data-testid="settings-onboarding-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="rounded-full bg-primary/10 p-2">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Getting started</h2>
                <p className="text-sm text-muted-foreground">
                  Replay the product tour or load a sample dataset anytime
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start justify-between py-3 gap-4">
                <div className="flex-1">
                  <h3 className="text-base font-medium mb-1">Replay product tour</h3>
                  <p className="text-sm text-muted-foreground">
                    A 60-second guided walkthrough of the dashboard
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate('/app');
                    setTimeout(() => startProductTour(), 600);
                  }}
                  className="ml-4 flex-shrink-0"
                  data-testid="settings-restart-tour-button"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Start tour
                </Button>
              </div>

              <Separator />

              <div className="flex items-start justify-between py-3 gap-4">
                <div className="flex-1">
                  <h3 className="text-base font-medium mb-1">Load sample dataset</h3>
                  <p className="text-sm text-muted-foreground">
                    Adds 13 curated indie-hacker signals to your current workspace. Free — won't count toward your AI quota.
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <SampleDataButton variant="outline" label="Load sample data" />
                </div>
              </div>
            </div>
          </Card>

          {/* Public Sharing Section */}
          <PublicSharingCard />

          {/* Data & Privacy Section */}
          <Card className="p-6" data-testid="settings-data-privacy-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="rounded-full bg-primary/10 p-2">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Data & Privacy</h2>
                <p className="text-sm text-muted-foreground">
                  Control your data and account
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Export Data */}
              <div className="flex items-start justify-between py-3">
                <div className="flex-1">
                  <h3 className="text-base font-medium mb-1">Export Your Data</h3>
                  <p className="text-sm text-muted-foreground">
                    Download all your feedback, decisions, and analytics as JSON
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleExportData}
                  disabled={isExporting}
                  className="ml-4"
                  data-testid="settings-export-data-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </div>

              <Separator />

              {/* Delete Account */}
              <div className="flex items-start justify-between py-3">
                <div className="flex-1">
                  <h3 className="text-base font-medium mb-1 text-destructive">Delete Account</h3>
                  <p className="text-sm text-muted-foreground">
                    Permanently delete your account and all associated data
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  className="ml-4"
                  data-testid="settings-delete-account-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>

              <Separator />

              {/* Privacy Links */}
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-3">
                  <strong className="text-foreground">Your Rights:</strong> You have full control over your data.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <a href="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </a>
                  <a href="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </a>
                  <a href="mailto:support@vibesprint.ai" className="text-primary hover:underline">
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </main>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="settings-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Account?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. All your data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-foreground">
                <strong>What will be deleted:</strong>
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                <li>All feedback signals and imported data</li>
                <li>AI-generated decisions and insights</li>
                <li>Roadmap and changelog items</li>
                <li>Analytics and trend data</li>
                <li>Account preferences</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              data-testid="settings-delete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAccount}
              data-testid="settings-delete-confirm-button"
            >
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer variant="minimal" />
    </div>
  );
};

export default SettingsPage;
